import React from "react";
import { Button, Stack, Typography } from "@mui/material";
import LogFormShell from "./LogFormShell";
import colors from "../../assets/theme/colors";

/**
 * Reusable, domain-agnostic "View source" sheet for any Handbook chapter's
 * external-record entries. Receives presentation-ready data only - it never
 * inspects facts, queries Firestore, resolves sourceRefs, or knows which
 * chapter/domain it's being used from. The owning chapter (e.g.
 * ProfileCommunicationChapter) resolves a sourceAction + fact into these props
 * before rendering this component.
 */
const HandbookSourceViewer = ({
  open,
  title = "Original source",
  metadataLabel,
  body,
  imageUrl,
  imageAlt = "Original source",
  documentUrl,
  documentName = "Original document",
  isLoading = false,
  isAvailable,
  unavailableMessage = "This source is unavailable right now. It may have been removed or isn't reachable from here.",
  onClose,
  onAnnotate,
  annotateLabel = "Add a note",
}) => {
  if (!open) return null;

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      forceDrawer
      footer={(
        <Stack spacing={0.75}>
          {onAnnotate ? (
            <Button
              variant="outlined"
              onClick={onAnnotate}
              fullWidth
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999 }}
            >
              {annotateLabel}
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={onClose}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999 }}
          >
            Close
          </Button>
        </Stack>
      )}
    >
      {isLoading ? (
        <Typography sx={{ fontSize: "0.94rem", color: colors.semantic.ink2 }}>
          Loading original source…
        </Typography>
      ) : isAvailable ? (
        <Stack spacing={1.25}>
          {metadataLabel ? (
            <Typography sx={{ fontSize: "0.82rem", color: colors.semantic.ink2 }}>
              {metadataLabel}
            </Typography>
          ) : null}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={imageAlt}
              style={{ display: "block", width: "100%", height: "auto", borderRadius: 12 }}
            />
          ) : null}
          {documentUrl ? (
            <Button
              component="a"
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 800, borderRadius: 999 }}
            >
              {`Open ${documentName || "original document"}`}
            </Button>
          ) : null}
          {body ? (
            <Typography sx={{ fontSize: "0.98rem", lineHeight: 1.6, color: colors.semantic.ink, whiteSpace: "pre-wrap" }}>
              {body}
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: "0.94rem", color: colors.semantic.ink2 }}>
          {unavailableMessage}
        </Typography>
      )}
    </LogFormShell>
  );
};

export default HandbookSourceViewer;
